import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { SoftwareInfo } from "@sscs/types";
import { Button, Dialog, Form, LoadingDots, Toast } from "@sscs/ui";
import { humanReadableBytes } from "@sscs/utils";
import { SoftwareInfo as CommonSoftwareInfo } from "Components";
import { FILE_SOFTWARE_INFO_DATA, VERSION_SOFTWARE_INFO_DATA } from "Constants";
import { useFileUploadChecks } from "Hooks";
import {
  FileSoftwareInfo,
  FileType,
  PredecessorOptions,
  VersionSoftwareInfo,
} from "Types";
import {
  getFileSoftwareInfoFormSchema,
  getVersionSoftwareInfoFormSchema,
} from "Utils";
import FileUpload from "../../components/FileUpload/FileUpload";
import FileVerification from "../../components/FileVerification/FileVerification";
import UploadIssues from "../../components/UploadIssues/UploadIssues";
import { FILE_NAME_CHARACTERS_LIMIT } from "../../constants";
import useCheckFileDuplicates from "../../hooks/useCheckFileDuplicates";
import {
  fileExtensionHasLimitedSupport,
  isFileExtensionSupported,
} from "../../utils";
import styles from "./uploadDialog.module.scss";

type Predecessor = { id: string } & SoftwareInfo;

const generateDefaults = ({
  fileType,
  predecessor,
}: {
  fileType: FileType;
  predecessor?: Predecessor | null;
}): FileSoftwareInfo | VersionSoftwareInfo => {
  if (predecessor) {
    return {
      ...VERSION_SOFTWARE_INFO_DATA,
      ...predecessor,
      predecessor_id: predecessor.id,
      version: "",
      release_date: null,
    };
  }

  return fileType === "VERSION"
    ? VERSION_SOFTWARE_INFO_DATA
    : FILE_SOFTWARE_INFO_DATA;
};

interface CommonDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FilestreamDialogProps extends CommonDialogProps {
  fileType: "FILESTREAM";
  predecessor?: never;
  predecessorOptions?: never;
  versionValues?: never;
  maxVersionsLimit?: never;
  onUpload: (file: File, softwareInfo: FileSoftwareInfo) => Promise<void>;
}

interface ProjectsDialogProps extends CommonDialogProps {
  fileType: "VERSION";
  predecessor?: Predecessor | null;
  predecessorOptions: PredecessorOptions;
  versionValues: string[];
  maxVersionsLimit: number;
  onUpload: (file: File, softwareInfo: VersionSoftwareInfo) => Promise<void>;
}

interface UploadDialogProps extends CommonDialogProps {
  fileType: FileType;
  predecessor?: Predecessor | null;
  predecessorOptions?: PredecessorOptions;
  versionValues?: string[];
  maxVersionsLimit?: number;
  onUpload: (
    file: File,
    softwareInfo: FileSoftwareInfo | VersionSoftwareInfo,
  ) => Promise<void>;
}

const UploadDialogContent = ({
  fileType,
  predecessor,
  predecessorOptions,
  versionValues,
  maxVersionsLimit,
  onClose,
  onUpload,
}: Omit<UploadDialogProps, "isOpen">): React.JSX.Element => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const {
    checksAreLoading,
    hasSufficientCapacity,
    isOverSizeLimit,
    remainingCapacity,
    sizeLimit,
  } = useFileUploadChecks(fileType, uploadedFile?.size ?? null);
  const duplicateCheck = useCheckFileDuplicates(
    uploadedFile && !isOverSizeLimit ? uploadedFile : undefined,
    fileType,
  );

  const formMethods = useForm<FileSoftwareInfo | VersionSoftwareInfo>({
    resolver: yupResolver(
      fileType === "FILESTREAM"
        ? getFileSoftwareInfoFormSchema()
        : getVersionSoftwareInfoFormSchema(versionValues ?? []),
    ),
    defaultValues: generateDefaults({ fileType, predecessor }),
  });

  const { handleSubmit, reset: resetForm, setValue, watch } = formMethods;

  const selectedPredecessorId = watch("predecessor_id")
    ? watch("predecessor_id")
    : undefined;
  const isFileNameTooLong = uploadedFile
    ? uploadedFile.name.length > FILE_NAME_CHARACTERS_LIMIT
    : false;

  const fileHasUnsupportedExtension = uploadedFile
    ? !isFileExtensionSupported(uploadedFile.name)
    : false;

  const fileHasLimitedSupport = uploadedFile
    ? fileExtensionHasLimitedSupport(uploadedFile.name)
    : false;

  const hasWarnings = fileHasUnsupportedExtension || fileHasLimitedSupport;
  const hasErrors =
    !hasSufficientCapacity || isOverSizeLimit || isFileNameTooLong;
  const hasIssues = hasWarnings || hasErrors;

  useEffect(() => {
    const derivedVersion = predecessorOptions?.find(
      (item) => item.id === selectedPredecessorId,
    );

    if (derivedVersion && uploadedFile) {
      const predecessor = {
        ...derivedVersion.softwareInfo,
        product: derivedVersion.softwareInfo.product,
        id: derivedVersion.id,
      };

      const derivedChangeSoftwareInfo = generateDefaults({
        fileType,
        predecessor,
      });

      resetForm(derivedChangeSoftwareInfo, {
        keepDirty: true,
        keepDirtyValues: true,
      });
    }
  }, [
    selectedPredecessorId,
    predecessorOptions,
    fileType,
    resetForm,
    uploadedFile,
  ]);

  const onSubmit: SubmitHandler<
    FileSoftwareInfo | VersionSoftwareInfo
  > = async (data) => {
    let onUploadPromise: Promise<void> | undefined;

    if (!uploadedFile) {
      return;
    }

    if (hasSufficientCapacity) {
      if (!isOverSizeLimit) {
        onUploadPromise = onUpload(uploadedFile, data);
      } else {
        Toast.show({
          title: `Selected file size ${humanReadableBytes(
            uploadedFile.size,
          )} exceeds upload limit (${humanReadableBytes(sizeLimit)})`,
          type: "error",
        });
      }
    } else {
      Toast.show({
        title: "Insufficient capacity",
        description: (
          <>
            Selected file ({humanReadableBytes(uploadedFile.size)}) is larger
            than remaining analysis capacity (
            {humanReadableBytes(remainingCapacity)})
          </>
        ),
        type: "error",
      });
    }

    onClose();

    await onUploadPromise;
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>
          {fileType === "FILESTREAM" ? "File" : "Version"} Upload
        </Dialog.Header.Title>
      </Dialog.Header>
      <Dialog.Body>
        <Dialog.Section>
          <Dialog.Section.Content>
            {checksAreLoading ? (
              <LoadingDots />
            ) : (
              <FileUpload
                title={
                  fileType === "FILESTREAM"
                    ? "Upload a file"
                    : "Upload a version"
                }
                file={uploadedFile}
                sha256={duplicateCheck.fileHash}
                onFileSelect={(file) => {
                  setUploadedFile(file);
                  setValue("product", file.name, { shouldValidate: true });
                }}
                onFileRemove={() => {
                  setUploadedFile(null);
                  resetForm();
                }}
              />
            )}
          </Dialog.Section.Content>
        </Dialog.Section>
        {uploadedFile ? (
          <FormProvider {...formMethods}>
            <Form.Form
              id="software-info"
              className={styles["body-rows"]}
              onSubmit={handleSubmit(onSubmit)}
              horizontal
              dense
            >
              {hasIssues && (
                <UploadIssues
                  uploadedFile={uploadedFile}
                  hasInsufficientCapacity={!hasSufficientCapacity}
                  remainingUsage={remainingCapacity}
                  fileHasUnsupportedExtension={fileHasUnsupportedExtension}
                  fileHasLimitedSupport={fileHasLimitedSupport}
                  isOverSizeLimit={isOverSizeLimit}
                  isFileNameTooLong={isFileNameTooLong}
                  uploadLimit={sizeLimit}
                />
              )}
              {fileType === "VERSION" ? (
                <CommonSoftwareInfo.Form
                  fileName={uploadedFile.name}
                  predecessors={predecessorOptions}
                  maxVersionsLimit={maxVersionsLimit}
                  isVersion
                />
              ) : (
                <CommonSoftwareInfo.Form
                  fileName={uploadedFile.name}
                  isVersion={false}
                />
              )}
              {!isOverSizeLimit && (
                <FileVerification
                  uploadedFile={uploadedFile}
                  uploadType={fileType}
                  duplicateCheck={duplicateCheck}
                />
              )}
            </Form.Form>
          </FormProvider>
        ) : null}
      </Dialog.Body>
      <Dialog.Footer>
        <Dialog.Footer.Actions>
          <Button id="cancel" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            id="upload"
            type="submit"
            color="primary"
            form="software-info"
            disabled={!uploadedFile}
          >
            Upload
          </Button>
        </Dialog.Footer.Actions>
      </Dialog.Footer>
    </>
  );
};

function UploadDialog(props: FilestreamDialogProps): React.JSX.Element;
function UploadDialog(props: ProjectsDialogProps): React.JSX.Element;
function UploadDialog({
  isOpen,
  ...props
}: UploadDialogProps): React.JSX.Element {
  return (
    <Dialog.Frame isOpen={isOpen} onClose={props.onClose} size="md">
      <UploadDialogContent {...props} />
    </Dialog.Frame>
  );
}

export default UploadDialog;
